import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { BellIcon, CloseIcon, CheckIcon, XCircleIcon, BuildingIcon, ClipboardIcon } from './Icons';
import '../styles/PromotionNotificationIcon.css';

interface Notification {
  id: string;
  type: 'opportunity_submitted' | 'promotion_submitted' | 'opportunity_approved' | 'promotion_approved' | 'opportunity_rejected' | 'promotion_rejected';
  relatedEntityId: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

const PromotionNotificationIcon: React.FC = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const API_URL = BACKEND_URL;
  const token = localStorage.getItem('token');

  const showFeedback = (type: 'success' | 'error' | 'info', message: string) => {
    setFeedback({ type, message });
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }
    feedbackTimeoutRef.current = setTimeout(() => {
      setFeedback(null);
    }, 3500);
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Use new dedicated endpoint that returns only "submitted" notifications
      const response = await axios.get(`${API_URL}/api/notifications/pending-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setNotifications(response.data || []);
    } catch (error) {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showDropdown) {
      fetchNotifications();
    }
  }, [showDropdown]);

  // Refresh on interval
  useEffect(() => {
    const interval = setInterval(() => {
      fetchNotifications();
    }, 15000); // Refresh every 15 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
      }
    };
  }, []);

  const fetchDetails = async (notification: Notification) => {
    try {
      let endpoint = '';
      if (notification.type === 'opportunity_submitted') {
        endpoint = `${API_URL}/api/opportunities/${notification.relatedEntityId}`;
      } else if (notification.type === 'promotion_submitted') {
        endpoint = `${API_URL}/api/promotions/${notification.relatedEntityId}`;
      }

      if (endpoint) {
        const response = await axios.get(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setSelectedDetails(response.data);
        setShowDetails(true);
      }
    } catch (error) {
      showFeedback('error', 'Failed to load request details. Please try again.');
    }
  };

  const getNotificationIconComponent = (type: string) => {
    if (type.includes('opportunity')) return <BuildingIcon size={18} color="#00ffff" />;
    if (type.includes('promotion')) return <ClipboardIcon size={18} color="#ff00ff" />;
    return <BellIcon size={18} color="#00ffff" />;
  };

  const handleApproveRequest = async (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const notification = notifications.find(n => n.id === notificationId);
      if (!notification) {
        showFeedback('error', 'This notification is no longer available.');
        return;
      }

      let endpoint = '';
      let entityType = '';
      if (notification.type === 'opportunity_submitted') {
        endpoint = `${API_URL}/api/opportunities/${notification.relatedEntityId}/approve`;
        entityType = 'Opportunity';
      } else if (notification.type === 'promotion_submitted') {
        endpoint = `${API_URL}/api/promotions/${notification.relatedEntityId}/approve`;
        entityType = 'Promotion';
      }

      if (!endpoint) {
        showFeedback('error', 'Unsupported request type.');
        return;
      }

      setNotifications(prev => prev.filter(req => req.id !== notificationId));

      const response = await axios.patch(endpoint, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 200) {
        // Hard-delete the specific pending notification to avoid any lingering entry.
        await axios.delete(`${API_URL}/api/notifications/${notificationId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        showFeedback('success', `${entityType} approved and published successfully.`);
        setTimeout(() => fetchNotifications(), 500);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to approve request';
      
      if (error.response?.status === 404) {
        showFeedback('info', 'This request was already processed.');
        fetchNotifications();
      } else {
        showFeedback('error', `Approval failed: ${errorMessage}`);
        fetchNotifications();
      }
    }
  };

  const handleRejectRequest = async (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const notification = notifications.find(n => n.id === notificationId);
      if (!notification) {
        showFeedback('error', 'This notification is no longer available.');
        return;
      }

      let endpoint = '';
      let entityType = '';
      if (notification.type === 'opportunity_submitted') {
        endpoint = `${API_URL}/api/opportunities/${notification.relatedEntityId}/reject`;
        entityType = 'Opportunity';
      } else if (notification.type === 'promotion_submitted') {
        endpoint = `${API_URL}/api/promotions/${notification.relatedEntityId}/reject`;
        entityType = 'Promotion';
      }

      if (!endpoint) {
        showFeedback('error', 'Unsupported request type.');
        return;
      }

      showFeedback('info', `Rejecting ${entityType.toLowerCase()} request. This action cannot be undone.`);

      setNotifications(prev => prev.filter(req => req.id !== notificationId));

      const response = await axios.patch(endpoint, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        // Hard-delete the specific pending notification to ensure it vanishes immediately.
        await axios.delete(`${API_URL}/api/notifications/${notificationId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        showFeedback('success', `${entityType} rejected and permanently removed.`);
        setTimeout(() => fetchNotifications(), 500);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to reject request';
      
      if (error.response?.status === 404) {
        showFeedback('info', 'This request was already processed.');
        fetchNotifications();
      } else {
        showFeedback('error', `Rejection failed: ${errorMessage}`);
        fetchNotifications();
      }
    }
  };

  return (
    <div className="promotion-notification-container">
      <motion.button
        className="notification-icon-btn"
        onClick={() => setShowDropdown(!showDropdown)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        title="Pending Requests"
      >
        <BellIcon size={20} color="#00ffff" />
        {notifications.length > 0 && (
          <motion.span
            className="notification-badge"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          >
            {notifications.length}
          </motion.span>
        )}
      </motion.button>

      <AnimatePresence>
        {showDropdown && (
          <>
            <motion.div
              className="notification-dropdown"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <div className="notification-header">
                <h3>Pending Requests</h3>
                <button
                  className="close-btn"
                  onClick={() => setShowDropdown(false)}
                  title="Close"
                >
                  <CloseIcon size={14} />
                </button>
              </div>

              {feedback && (
                <div className={`notification-feedback ${feedback.type}`}>
                  {feedback.message}
                </div>
              )}

              {loading ? (
                <div className="notification-loading">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="notification-empty">
                  No pending requests
                </div>
              ) : (
                <div className="notification-list">
                  {notifications.map((notification) => (
                    <motion.div
                      key={notification.id}
                      className={`notification-item ${notification.type.split('_')[0]}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="item-icon">
                        {getNotificationIconComponent(notification.type)}
                      </div>
                      <div className="item-content">
                        <h4 className="item-title">{notification.title}</h4>
                        <p className="item-message">{notification.message}</p>
                        <span className="item-date">
                          {new Date(notification.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="item-actions">
                        <motion.button
                          className="action-btn details-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchDetails(notification);
                          }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          title="View Details"
                        >
                          DETAILS
                        </motion.button>
                        <motion.button
                          className="action-btn approve-btn"
                          onClick={(e) => handleApproveRequest(notification.id, e)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          title="Approve"
                        >
                          <CheckIcon size={16} color="#4ade80" />
                        </motion.button>
                        <motion.button
                          className="action-btn reject-btn"
                          onClick={(e) => handleRejectRequest(notification.id, e)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          title="Reject"
                        >
                          <XCircleIcon size={16} color="#f87171" />
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Overlay to close dropdown */}
            <motion.div
              className="notification-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDropdown(false)}
            />
          </>
        )}
      </AnimatePresence>

      {/* Details Modal */}
      <AnimatePresence>
        {showDetails && selectedDetails && (
          <>
            <motion.div
              className="details-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDetails(false)}
            />
            <motion.div
              className="details-modal"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="details-header">
                <h2>{selectedDetails.title || selectedDetails.name}</h2>
                <button
                  className="close-btn"
                  onClick={() => setShowDetails(false)}
                  title="Close"
                >
                  <CloseIcon size={16} />
                </button>
              </div>
              <div className="details-content">
                <div className="detail-group">
                  <label>Description:</label>
                  <p>{selectedDetails.description}</p>
                </div>
                {selectedDetails.company && (
                  <div className="detail-group">
                    <label>Company:</label>
                    <p>{selectedDetails.company}</p>
                  </div>
                )}
                {selectedDetails.stipend && (
                  <div className="detail-group">
                    <label>Stipend:</label>
                    <p>{selectedDetails.stipend}</p>
                  </div>
                )}
                {selectedDetails.deadline && (
                  <div className="detail-group">
                    <label>Deadline:</label>
                    <p>{new Date(selectedDetails.deadline).toLocaleDateString()}</p>
                  </div>
                )}
                {selectedDetails.link && (
                  <div className="detail-group">
                    <label>Link:</label>
                    <a href={selectedDetails.link} target="_blank" rel="noopener noreferrer" className="detail-link">
                      {selectedDetails.link}
                    </a>
                  </div>
                )}
                {selectedDetails.type && (
                  <div className="detail-group">
                    <label>Type:</label>
                    <p className="type-badge">{selectedDetails.type}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PromotionNotificationIcon;
